import React, { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import styled from '@emotion/styled';
import { spacing, SpacingProps } from '@mui/system';

import { Chip as MuiChip, IconButton, Table, TableBody, TableContainer, TablePagination, TableRow } from '@mui/material';
import { Launch as LaunchIcon } from '@mui/icons-material';

import { InvoiceListItem } from '../../../apis/invoiceApi';
import { getInvoiceDisplayStatus, getInvoiceStatus, invoiceStatusColors } from '../utils/invoiceUtils';
import { stableSort } from '../../../utils/listUtils';
import { currencyFormat, formatDate } from '../../../utils/formatUtils';

import { EnhancedTableHead, HeadCell } from '../../../components/tables/EnhandedTableHead';
import PaddedCell from '../../../components/tables/PaddedCell';
import PaddedTableResults from '../../../components/common/PaddedTableResults';
import InvoiceEmptyResults from './InvoiceEmptyResults';

interface ChipProps extends SpacingProps {
  overdue?: number;
  paid?: number;
  partialPaid?: number;
  sent?: number;
  voided?: number;
}

const Chip = styled(MuiChip)<ChipProps>`
  ${spacing};
  background: ${(props) => props.overdue && invoiceStatusColors.overdue};
  background: ${(props) => props.paid && invoiceStatusColors.paid};
  background: ${(props) => props.partialPaid && invoiceStatusColors.partialPaid};
  background: ${(props) => props.sent && invoiceStatusColors.sent};
  background: ${(props) => props.voided && invoiceStatusColors.voided};
  color: ${(props) => props.theme.palette.common.white};
`;

const headCells: Array<HeadCell> = [
  { id: 'status', alignment: 'left', label: 'Status' },
  { id: 'amountDue', alignment: 'right', label: 'Amount Due' },
  { id: 'dueDate', alignment: 'left', label: 'Due Date' },
  { id: 'invoiceNumber', alignment: 'left', label: 'Number' },
  { id: 'total', alignment: 'right', label: 'Total' },
  { id: 'actions', alignment: 'right', label: 'Actions' },
];

interface InvoiceTableProps {
  isLoading: boolean;
  rows: InvoiceListItem[];
  backOfficeRootUrl: string | null;
  hasBillingRole: boolean;
}

function InvoiceTable({ isLoading, rows, hasBillingRole, backOfficeRootUrl }: InvoiceTableProps) {
  const [order, setOrder] = React.useState<'desc' | 'asc'>('desc');
  const [orderBy, setOrderBy] = React.useState('invoiceNumber');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  useEffect(() => {
    setPage(0);
  }, [rows]);

  const handleRequestSort = (_event: any, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (id: string) => {
    window.open(`${backOfficeRootUrl}/Portal/Customers/Invoices/${id}`, '_blank');
  };

  return (
    <>
      <TableContainer>
        <Table aria-labelledby="tableTitle" size="small" aria-label="enhanced table">
          <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} headCells={headCells} />
          <TableBody>
            {stableSort(rows, orderBy, order)
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                const invoiceStatus = getInvoiceStatus(row);
                const statusToLower = invoiceStatus.toLowerCase();
                const displayStatus = getInvoiceDisplayStatus(row);
                return (
                  <TableRow hover role="row" tabIndex={-1} key={`${row.id}-${index}`} onClick={() => handleRowClick(row.id)}>
                    <PaddedCell sx={{ pt: 3 }}>
                      {statusToLower === 'overdue' && <Chip size="small" mr={1} mb={1} label={displayStatus} overdue={+true} />}
                      {statusToLower === 'paid' && <Chip size="small" mr={1} mb={1} label={displayStatus} paid={+true} />}
                      {statusToLower === 'partialpaid' && <Chip size="small" mr={1} mb={1} label={displayStatus} partialPaid={+true} />}
                      {statusToLower === 'sent' && <Chip size="small" mr={1} mb={1} label={displayStatus} sent={+true} />}
                      {statusToLower === 'voided' && <Chip size="small" mr={1} mb={1} label={displayStatus} voided={+true} />}
                    </PaddedCell>
                    <PaddedCell align="right">{currencyFormat.format(row.amountDue)}</PaddedCell>
                    <PaddedCell>{formatDate(row.dueDate)}</PaddedCell>
                    <PaddedCell>{row.invoiceNumber}</PaddedCell>
                    <PaddedCell align="right">{currencyFormat.format(row.total)}</PaddedCell>
                    <PaddedCell align="right">
                      <IconButton
                        sx={{ pt: 1, pb: 1, color: (theme) => theme.palette.primary.main }}
                        aria-label="details"
                        component={NavLink}
                        to={`${backOfficeRootUrl}/Portal/Customers/Invoices/${row.id}`}
                        size="medium"
                        target="_blank"
                      >
                        <LaunchIcon />
                      </IconButton>
                    </PaddedCell>
                  </TableRow>
                );
              })}
            <PaddedTableResults rowsPerPage={rowsPerPage} pageNumber={page} rowCount={rows.length} isLoading={isLoading} />
          </TableBody>
        </Table>
        <InvoiceEmptyResults hasRows={rows.length > 0} hasBillingRole={hasBillingRole} isLoading={isLoading} useTablePresets={true} />
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Rows/page:"
      />
    </>
  );
}

export default InvoiceTable;
